import React, { useMemo, useState } from "react";
import { midiToKey, playNote, audioSupported } from "../../lib/keyboardPlan";
import { scaleWalk, playMidiSeq, STEP_HINTS } from "../../lib/numberSenseV2";
import { Icon, Keys, Piano, TypingHint, MidiBadge, useTypingPiano } from "../numberSenseUi";
import useMidi from "../numberSense/useMidi";

const CAN_PLAY = audioSupported();

/* ============================================================
   Number Sense v2 — the guided scale walk: build this key's scale
   on the piano yourself, one step at a time, following the W/H
   recipe ("find Bb — that's home… now a whole step up…"). Runs
   the first time an undrilled key is opened, and stays replayable
   from the drill menu, so you've physically built the scale once
   before the drills quiz you on it.

   Octave-exact on purpose: the step is "up from where you are",
   so the same pitch class an octave away doesn't count. Two
   misses light up the target key.

   onDone() fires from the finish screen's CTA; onClose backs out.
   ============================================================ */

const keyId = (midi) => {
  const k = midiToKey(midi);
  return `${k.name}${k.octave}`;
};

const WalkScreen = ({ type, keyName, onDone, onClose }) => {
  const walk = useMemo(() => scaleWalk(type.id, keyName), [type.id, keyName]);
  const [idx, setIdx] = useState(0);
  const [misses, setMisses] = useState(0);
  const [wrong, setWrong] = useState(false);

  const steps = walk.steps;
  const done = idx >= steps.length;
  const current = done ? null : steps[idx];
  const placed = useMemo(
    () => new Set(steps.slice(0, idx).map((s) => keyId(s.midi))),
    [steps, idx]
  );

  const press = (k) => {
    if (done) return;
    if (k.midi === current.midi) {
      setWrong(false);
      setMisses(0);
      const next = idx + 1;
      setIdx(next);
      if (next >= steps.length && CAN_PLAY) {
        // the payoff: hear the whole thing you just built
        window.setTimeout(() => playMidiSeq(steps.map((s) => s.midi), 280), 350);
      }
    } else {
      setWrong(true);
      setMisses((m) => m + 1);
    }
  };

  // Typing keyboard and MIDI feed the same walk (Piano taps sound themselves;
  // typing needs the note played here).
  const midi = useMidi(press, !done);
  useTypingPiano((k) => {
    playNote(k.midi);
    press(k);
  }, !done);

  const prev = idx > 0 ? steps[idx - 1] : null;

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onClose} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">walk: {walk.label}</div>
        </div>
        <div className="scroll">
          {/* the scale filling in as you build it */}
          <div className="teach-chips v2-walk-chips">
            {steps.map((s, i) => (
              <span
                className={`teach-chip ${i < idx ? "" : "v2-chip-dim"}`}
                key={`${s.degree}-${i}`}
              >
                <b className="mono">{s.degree}</b>
                <span>{i < idx ? s.name : "?"}</span>
              </span>
            ))}
          </div>

          {!done ? (
            <>
              <div className="v2-walk-banner">
                <div className="qkind">
                  step {idx + 1} of {steps.length}
                </div>
                {current.step === null ? (
                  <div className="qprompt">
                    find <b>{current.name}</b> — that's home, degree 1
                  </div>
                ) : (
                  <div className="qprompt">
                    from <b>{prev.name}</b>, go up a{" "}
                    <b className="mono">{current.step}</b>
                  </div>
                )}
                {current.step !== null && (
                  <div className="qprompt-sub muted">{STEP_HINTS[current.step]}</div>
                )}
                {wrong && (
                  <div className="v2-walk-msg">
                    not quite —{" "}
                    {current.step === null
                      ? `${current.name} is out there, look for it`
                      : STEP_HINTS[current.step]}
                    {misses >= 2 ? " (it's glowing now)" : ""}
                  </div>
                )}
              </div>
              <Piano
                pressed={new Set()}
                exactKeys={placed}
                exactHint={misses >= 2 ? new Set([keyId(current.midi)]) : null}
                onPress={press}
              />
              <TypingHint midiStatus={midi.status} />
              <MidiBadge status={midi.status} devices={midi.devices} />
            </>
          ) : (
            <>
              <div className="v2-walk-banner">
                <div className="qkind">that's the whole thing</div>
                <div className="qprompt">
                  you just built <b>{walk.label}</b>
                </div>
                <div className="qprompt-sub muted">
                  same recipe, this key's notes — now make it automatic
                </div>
              </div>
              <Piano pressed={new Set()} exactKeys={placed} onPress={() => {}} />
              <div className="v2-walk-actions">
                <button className="btn" onClick={onDone}>
                  Drill it
                </button>
                <button
                  className="btn ghost"
                  onClick={() => {
                    setIdx(0);
                    setMisses(0);
                    setWrong(false);
                  }}
                >
                  Walk it again
                </button>
              </div>
            </>
          )}

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalkScreen;
